import { AntennesUtilisateurApiObject } from '@europrocurement/l2d-domain';
import { Box, Card } from '@mui/material';
import React from 'react';
import {
    InformationCard,
    InformationCardItemProps,
    useModal,
} from '@europrocurement/flexy-components';
import { FlexyForm } from '@europrocurement/flexy-form';
import { AntennesJsonldAntenneUpdate } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { AntennaHeader } from '../AntennaHeader/AntennaHeader';
import { getAntennaFormStructure } from '../antennaFormStructure';
import { AntennaType } from '../../../views/Antennes/AntennesList';

type AntennaProps = {
    antenne: AntennesUtilisateurApiObject;
    handlePatch: (payload: AntennesJsonldAntenneUpdate) => void;
};

/**
 * Displays selected antenna informations.
 * @param antenne selected antenna object
 * @param handlePatch function : get payload and send patch request on API
 * @returns Antenna Header and InformationCard components
 */
export const AntennaCard: React.FunctionComponent<AntennaProps> = function ({
    antenne,
    handlePatch,
}) {
    const { modalActions } = useModal();

    /**
     * The function returns a string equivalent to the chip display in the InformationCard
     * @param status boolean data given
     * @returns string equivalent to the boolean value isDeleted
     */
    const getStatusValue = (status: boolean) => {
        if (status) {
            return 'Inactif';
        }

        return 'Actif';
    };

    /**
     * Antenna informations for the InformationCard component (array of objects {label, value})
     */
    const antennaInfos: Array<InformationCardItemProps> = [
        { label: 'Identifiant', value: antenne.idAntenne },
        { label: 'Libellé', value: antenne.libelle },
        { label: 'Statut', value: getStatusValue(antenne.isDeleted) },
    ];

    /**
     * default antenna values for the modfication form
     */
    const defaultValues: AntennaType = {
        idAntenne: antenne.idAntenne,
        libelle: antenne.libelle,
        isDeleted: antenne.isDeleted,
    };

    /**
     * On modification form submit, take data form return and send them to the API on PATCH request.
     * Verification before patch if the data have been modified to limits API calls.
     * @param formValues data sent on form submit
     */
    const handleFormSubmit = (formValues: AntennaType) => {
        const payload = formValues;

        if (payload.libelle !== antenne.libelle || payload.isDeleted !== antenne.isDeleted) {
            handlePatch(payload);
        }

        modalActions.close();
    };

    /**
     * Open a modal that contains the modification form (FlexyForm component)
     * Calls handleFormSubmit function on form submit
     */
    const handleModificationSubmit = () => {
        modalActions.call(
            <FlexyForm
                formObject={defaultValues}
                formStructure={getAntennaFormStructure()}
                onSubmit={handleFormSubmit}
            />,
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', margin: '0px 0px 18px 0px' }}>
            <AntennaHeader antenne={antenne} />

            <Card sx={{ width: '100%', margin: '10px 0 0 0', padding: '0' }}>
                <InformationCard
                    title="Informations de l'antenne"
                    button={{ label: 'Modifier', onClick: handleModificationSubmit }}
                    items={antennaInfos}
                    subtitle={false}
                />
            </Card>
        </Box>
    );
};
